import { useState, useEffect, useCallback } from 'react';
import OfflineStorageService, { type SyncStatus, type SyncQueueItem } from '../services/offlineStorage';
import { useNetworkStatus } from './useNetworkStatus';

export interface SyncQueueHook {
  syncStatus: SyncStatus;
  queueItems: SyncQueueItem[];
  isProcessing: boolean;
  forceSyncFile: (fileId: string) => Promise<void>;
  clearQueue: () => Promise<void>;
  retryFailedItems: () => Promise<void>;
  pauseSync: () => void;
  resumeSync: () => void;
  isPaused: boolean;
}

export const useSyncQueue = (): SyncQueueHook => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ status: 'idle', progress: 0, total: 0 });
  const [queueItems, setQueueItems] = useState<SyncQueueItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const { status: networkStatus } = useNetworkStatus();
  
  const offlineService = OfflineStorageService.getInstance();

  // Update sync status and queue items
  const updateSyncData = useCallback(() => {
    const status = offlineService.getSyncStatus();
    const items = offlineService.getSyncQueue();
    
    setSyncStatus(status);
    setQueueItems(items);
    setIsProcessing(status.status === 'syncing');
  }, [offlineService]);

  // Initialize sync listener
  useEffect(() => {
    const handleSyncStatusChange = (status: SyncStatus) => {
      setSyncStatus(status);
      setIsProcessing(status.status === 'syncing');
    };

    // Add listener for sync status changes
    offlineService.addSyncListener(handleSyncStatusChange);
    
    // Initial data load
    updateSyncData();

    // Set up periodic updates
    const interval = setInterval(updateSyncData, 5000); // Update every 5 seconds

    return () => {
      offlineService.removeSyncListener(handleSyncStatusChange);
      clearInterval(interval);
    };
  }, [offlineService, updateSyncData]);

  // Monitor network status changes
  useEffect(() => {
    if (networkStatus.isConnected && !isPaused) {
      // Network is back online and sync is not paused
      updateSyncData();
    }
  }, [networkStatus.isConnected, isPaused, updateSyncData]);

  // Force sync a specific file
  const forceSyncFile = useCallback(async (fileId: string): Promise<void> => {
    if (!networkStatus.isConnected) {
      throw new Error('No internet connection available');
    }
    
    if (isPaused) {
      throw new Error('Sync is currently paused');
    }

    try {
      await offlineService.forceSyncFile(fileId);
      updateSyncData();
    } catch (error) {
      console.error('[SyncQueue] Force sync failed:', error);
      throw error;
    }
  }, [offlineService, networkStatus.isConnected, isPaused, updateSyncData]);

  // Clear the entire sync queue
  const clearQueue = useCallback(async (): Promise<void> => {
    try {
      await offlineService.clearSyncQueue();
      updateSyncData();
    } catch (error) {
      console.error('[SyncQueue] Clear queue failed:', error);
      throw error;
    }
  }, [offlineService, updateSyncData]);

  // Retry failed items
  const retryFailedItems = useCallback(async (): Promise<void> => {
    if (!networkStatus.isConnected) {
      throw new Error('No internet connection available');
    }
    
    if (isPaused) {
      throw new Error('Sync is currently paused');
    }

    try {
      // Reset attempts for failed items
      const failedItems = queueItems.filter(item => 
        item.attempts >= 3 && item.error
      );
      
      for (const item of failedItems) {
        await offlineService.forceSyncFile(item.id);
      }
      
      updateSyncData();
    } catch (error) {
      console.error('[SyncQueue] Retry failed items failed:', error);
      throw error;
    }
  }, [offlineService, networkStatus.isConnected, isPaused, queueItems, updateSyncData]);

  // Pause sync processing
  const pauseSync = useCallback(() => {
    setIsPaused(true);
    console.log('[SyncQueue] Sync paused by user');
  }, []);

  // Resume sync processing
  const resumeSync = useCallback(() => {
    setIsPaused(false);
    console.log('[SyncQueue] Sync resumed by user');
    
    // Trigger sync if we're online
    if (networkStatus.isConnected) {
      updateSyncData();
    }
  }, [networkStatus.isConnected, updateSyncData]);

  return {
    syncStatus,
    queueItems,
    isProcessing,
    forceSyncFile,
    clearQueue,
    retryFailedItems,
    pauseSync,
    resumeSync,
    isPaused
  };
};

// Utility hook for simple sync status
export const useSyncStatus = (): SyncStatus => {
  const { syncStatus } = useSyncQueue();
  return syncStatus;
};

// Utility hook to check if there are pending syncs
export const useHasPendingSyncs = (): boolean => {
  const { queueItems } = useSyncQueue();
  return queueItems.length > 0;
};

export default useSyncQueue; 